/**
 * L7 — Big Number Graphic
 * Displays a large prominent statistic/number.
 * e.g. "₹5,000 करोड़", "10 मरे", "25% वृद्धि"
 */
import React from 'react';
import { AbsoluteFill, useCurrentFrame, spring, useVideoConfig } from 'remotion';
import type { SceneProps } from '../../types';
import { SceneContainer, SubheadlineText, GlobalOverlays } from './shared';

export const L7_BigNumberGraphic: React.FC<SceneProps> = (props) => {
  const { headline, bigNumber, subheadline, assets, colorPalette, subheadlineFont } = props;
  const primary = assets.primary;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const scale = spring({ frame, fps, config: { damping: 14, mass: 0.7, stiffness: 100 }, durationInFrames: 20 });
  const opacity = Math.min(frame / 8, 1);

  return (
    <SceneContainer bg={colorPalette.primary}>
      {/* Background visual if available — darkened */}
      {primary && (
        <AbsoluteFill style={{ opacity: 0.2 }}>
          <img src={primary.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </AbsoluteFill>
      )}

      {/* Geometric accents */}
      <AbsoluteFill style={{ overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600, height: 600, borderRadius: '50%',
          border: `1px solid ${colorPalette.accent}20`,
        }} />
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800, height: 800, borderRadius: '50%',
          border: `1px solid ${colorPalette.accent}10`,
        }} />
      </AbsoluteFill>

      {/* Center content */}
      <AbsoluteFill style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '80px 120px 120px',
        textAlign: 'center',
      }}>
        {/* Context label */}
        <div style={{
          fontFamily: 'Roboto, sans-serif', fontSize: 18, fontWeight: 600,
          color: colorPalette.accent, letterSpacing: 3,
          textTransform: 'uppercase', marginBottom: 24,
          opacity,
        }}>
          {headline}
        </div>

        {/* Big number — wrapped for layerKey "bigNumber" transform support via direct style.
            Note: L7 big number uses animated spring so we don't wrap with layerKey here,
            but headline text above it does benefit from subheadline layerKey below. */}
        <div style={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: 120, fontWeight: 900,
          color: '#fff',
          lineHeight: 1,
          transform: `scale(${scale})`,
          opacity: scale,
          textShadow: `0 0 60px ${colorPalette.accent}60`,
          letterSpacing: -2,
        }}>
          {bigNumber ?? '—'}
        </div>

        {/* Accent underline */}
        <div style={{
          marginTop: 20, height: 4, width: `${Math.min(scale * 100, 100)}px`,
          background: colorPalette.accent, borderRadius: 2,
        }} />

        {/* Subheadline context */}
        {subheadline && (
          <SubheadlineText
            text={subheadline}
            font={{ ...subheadlineFont, color: 'rgba(255,255,255,0.7)', fontSize: 26 }}
            delay={20}
            style={{ marginTop: 24, textAlign: 'center' }}
            layerKey="subheadline"
          />
        )}
      </AbsoluteFill>

      <GlobalOverlays props={props} />
    </SceneContainer>
  );
};
